import React, {
  useEffect,
  useState,
} from "react";

import { Link } from "react-router-dom";

import {
  getPublicMedia
} from "../services/publicApi";

import "../styles/Media.css";

import {
  BACKEND_URL
} from "../util/api";


const PublicMedia = () => {

  /*
  |--------------------------------------------------------------------------
  | STATE
  |--------------------------------------------------------------------------
  */

  const [media, setMedia] =
    useState([]);

  const [pagination, setPagination] =
    useState(null);

  const [page, setPage] =
    useState(1);

  const [folder, setFolder] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [selectedMedia, setSelectedMedia] =
    useState(null);


  const perPage = 12;


  /*
  |--------------------------------------------------------------------------
  | LOAD MEDIA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    const loadMedia = async () => {

      try {

        setLoading(true);

        setError("");


        const result =
          await getPublicMedia(
            page,
            perPage,
            folder
          );


        if (result.success) {

          setMedia(
            result.data?.data || []
          );

          setPagination(
            result.data || null
          );

        } else {

          setError(
            "Unable to load media."
          );

        }

      } catch (err) {

        console.error(err);

        setError(
          err.message ||
          "Unable to load media."
        );

      } finally {

        setLoading(false);

      }

    };


    loadMedia();

  }, [page, folder]);


  /*
  |--------------------------------------------------------------------------
  | FOLDER FILTER
  |--------------------------------------------------------------------------
  */

  const handleFolderChange = (
    value
  ) => {

    setFolder(value);

    setPage(1);

  };


  /*
  |--------------------------------------------------------------------------
  | LIGHTBOX
  |--------------------------------------------------------------------------
  */

  const closeLightbox = () => {

    setSelectedMedia(null);

  };


  /*
  |--------------------------------------------------------------------------
  | PAGINATION
  |--------------------------------------------------------------------------
  */

  const handlePageChange = (
    newPage
  ) => {

    if (!pagination) {
      return;
    }


    if (
      newPage < 1 ||
      newPage >
        pagination.last_page
    ) {
      return;
    }


    setPage(newPage);


    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  };


  /*
  |--------------------------------------------------------------------------
  | MEDIA URL
  |--------------------------------------------------------------------------
  */

  const getMediaUrl = (
    item
  ) => {

    if (!item?.file_path) {
      return "";
    }


    if (
      item.file_path.startsWith(
        "http://"
      ) ||
      item.file_path.startsWith(
        "https://"
      )
    ) {

      return item.file_path;

    }


    return (
      `${BACKEND_URL}/storage/${item.file_path}`
    );

  };


  /*
  |--------------------------------------------------------------------------
  | DETECT MEDIA TYPE
  |--------------------------------------------------------------------------
  */

  const getMediaType = (
    item
  ) => {

    /*
     * First use media_type from Laravel
     * if it exists.
     */

    if (item?.media_type) {

      return item.media_type;

    }


    /*
     * Fall back to MIME type.
     */

    const mime =
      item?.mime_type || "";


    if (
      mime.startsWith(
        "image/"
      )
    ) {

      return "image";

    }


    if (
      mime.startsWith(
        "video/"
      )
    ) {

      return "video";

    }


    return "document";

  };


  /*
  |--------------------------------------------------------------------------
  | TYPE HELPERS
  |--------------------------------------------------------------------------
  */

  const isImage = (
    item
  ) => {

    return (
      getMediaType(item) ===
      "image"
    );

  };


  const isVideo = (
    item
  ) => {

    return (
      getMediaType(item) ===
      "video"
    );

  };


  const isDocument = (
    item
  ) => {

    return (
      getMediaType(item) ===
      "document"
    );

  };


  /*
  |--------------------------------------------------------------------------
  | DOCUMENT TYPE
  |--------------------------------------------------------------------------
  */

  const getDocumentType = (
    item
  ) => {

    const mime =
      (
        item?.mime_type ||
        ""
      ).toLowerCase();


    const fileName =
      (
        item?.file_name ||
        ""
      ).toLowerCase();


    if (
      mime.includes("pdf") ||
      fileName.endsWith(".pdf")
    ) {

      return "PDF";

    }


    if (
      mime.includes("word") ||
      mime.includes(
        "officedocument.wordprocessingml"
      ) ||
      fileName.endsWith(".doc") ||
      fileName.endsWith(".docx")
    ) {

      return "DOC";

    }


    if (
      mime.includes("excel") ||
      mime.includes(
        "spreadsheet"
      ) ||
      mime.includes("csv") ||
      fileName.endsWith(".xls") ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".csv")
    ) {

      return "XLS";

    }


    if (
      mime.includes(
        "powerpoint"
      ) ||
      mime.includes(
        "presentation"
      ) ||
      fileName.endsWith(".ppt") ||
      fileName.endsWith(".pptx")
    ) {

      return "PPT";

    }


    if (
      mime.includes("zip") ||
      fileName.endsWith(".zip")
    ) {

      return "ZIP";

    }


    if (
      mime.includes("text") ||
      fileName.endsWith(".txt")
    ) {

      return "TXT";

    }


    return "FILE";

  };


  /*
  |--------------------------------------------------------------------------
  | MEDIA LABEL
  |--------------------------------------------------------------------------
  */

  const getMediaLabel = (
    item
  ) => {

    if (isImage(item)) {
      return "IMAGE";
    }


    if (isVideo(item)) {
      return "VIDEO";
    }


    return getDocumentType(item);

  };


  /*
  |--------------------------------------------------------------------------
  | DOCUMENT ICON
  |--------------------------------------------------------------------------
  */

  const getDocumentIcon = (
    item
  ) => {

    const type =
      getDocumentType(item);


    switch (type) {

      case "PDF":
        return "PDF";

      case "DOC":
        return "DOC";

      case "XLS":
        return "XLS";

      case "PPT":
        return "PPT";

      case "ZIP":
        return "ZIP";

      case "TXT":
        return "TXT";

      default:
        return "FILE";

    }

  };


  /*
  |--------------------------------------------------------------------------
  | DOWNLOAD MEDIA
  |--------------------------------------------------------------------------
  */

  const handleDownload = (
    item
  ) => {

    const url =
      getMediaUrl(item);


    if (!url) {
      return;
    }


    const link =
      document.createElement(
        "a"
      );


    link.href = url;

    link.download =
      item.file_name ||
      item.name ||
      "media";


    link.target = "_blank";

    link.rel = "noopener noreferrer";


    document.body.appendChild(
      link
    );


    link.click();


    document.body.removeChild(
      link
    );

  };


  /*
  |--------------------------------------------------------------------------
  | OPEN MEDIA
  |--------------------------------------------------------------------------
  */

  const handleOpenMedia = (
    item
  ) => {

    setSelectedMedia(item);

  };


  /*
  |--------------------------------------------------------------------------
  | KEYBOARD LIGHTBOX SUPPORT
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    const handleKeyDown = (
      event
    ) => {

      if (
        event.key === "Escape" &&
        selectedMedia
      ) {

        closeLightbox();

      }

    };


    document.addEventListener(
      "keydown",
      handleKeyDown
    );


    return () => {

      document.removeEventListener(
        "keydown",
        handleKeyDown
      );

    };

  }, [selectedMedia]);


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (

    <main className="media-page">

      {/* =========================================================
          HERO
      ========================================================= */}

      <section className="media-hero">

        <div className="container">

          <Link
            to="/"
            className="media-back-link"
          >
            ← Back to Home
          </Link>


          <div className="media-hero-content">

            <p className="section-label light">

              <span />

              MEDIA CENTRE

            </p>


            <h1>

              Moments of{" "}

              <em>
                service.
              </em>

            </h1>


            <p>

              Explore photographs,
              videos, documents and
              visual records documenting
              legislative activities,
              constituency engagements,
              community programmes and
              public service.

            </p>

          </div>

        </div>

      </section>


      {/* =========================================================
          MEDIA CONTENT
      ========================================================= */}

      <section className="media-content-section">

        <div className="container">

          {/* HEADER */}

          <div className="media-toolbar">

            <div>

              <p className="section-label">

                <span />

                MEDIA ARCHIVE

              </p>


              <h2>

                In the{" "}

                <em>
                  community.
                </em>

              </h2>

            </div>


            {/* FILTER */}

            <div className="media-filter">

              <label htmlFor="media-folder">

                FILTER

              </label>


              <select
                id="media-folder"
                value={folder}
                onChange={(e) =>
                  handleFolderChange(
                    e.target.value
                  )
                }
              >

                <option value="">
                  All Media
                </option>


                <option value="general">
                  General
                </option>


                <option value="constituency">
                  Constituency
                </option>


                <option value="legislative">
                  Legislative
                </option>


                <option value="projects">
                  Projects
                </option>


                <option value="events">
                  Events
                </option>

              </select>

            </div>

          </div>


          {/* =====================================================
              LOADING
          ===================================================== */}

          {loading && (

            <div className="media-loading">

              <div className="media-spinner" />


              <p>
                Loading media...
              </p>

            </div>

          )}


          {/* =====================================================
              ERROR
          ===================================================== */}

          {!loading &&
            error && (

              <div className="media-error">

                <h3>
                  Unable to load media
                </h3>


                <p>
                  {error}
                </p>


                <button
                  type="button"
                  onClick={() =>
                    setPage(page)
                  }
                >
                  Try Again
                </button>

              </div>

            )}


          {/* =====================================================
              EMPTY
          ===================================================== */}

          {!loading &&
            !error &&
            media.length === 0 && (

              <div className="media-empty">

                <div className="media-empty-icon">
                  ◎
                </div>


                <h3>
                  No media found
                </h3>


                <p>
                  There are currently no
                  media files available
                  in this collection.
                </p>

              </div>

            )}


          {/* =====================================================
              GRID
          ===================================================== */}

          {!loading &&
            !error &&
            media.length > 0 && (

              <>

                <div className="media-grid">

                  {media.map(
                    (item) => {

                      const mediaUrl =
                        getMediaUrl(
                          item
                        );


                      const type =
                        getMediaType(
                          item
                        );


                      const label =
                        getMediaLabel(
                          item
                        );


                      return (

                        <article
                          className={`media-card media-card-${type}`}
                          key={item.id}
                          onClick={() =>
                            handleOpenMedia(
                              item
                            )
                          }
                        >

                          {/* =================================================
                              MEDIA PREVIEW
                          ================================================= */}

                          <div className="media-card-image">

                            {/* IMAGE */}

                            {isImage(
                              item
                            ) && (

                              mediaUrl ? (

                                <img
                                  src={mediaUrl}
                                  alt={
                                    item.alt_text ||
                                    item.name
                                  }
                                  loading="lazy"
                                />

                              ) : (

                                <div className="media-image-placeholder">
                                  No Image
                                </div>

                              )

                            )}


                            {/* VIDEO */}

                            {isVideo(
                              item
                            ) && (

                              mediaUrl ? (

                                <video
                                  src={mediaUrl}
                                  preload="metadata"
                                  muted
                                  playsInline
                                />

                              ) : (

                                <div className="media-image-placeholder">
                                  No Video
                                </div>

                              )

                            )}


                            {/* DOCUMENT */}

                            {isDocument(
                              item
                            ) && (

                              <div className="media-document-card">

                                <div className="media-document-icon">

                                  {getDocumentIcon(
                                    item
                                  )}

                                </div>


                                <div className="media-document-name">

                                  {item.file_name ||
                                    item.name}

                                </div>


                                <span>
                                  {label}
                                </span>

                              </div>

                            )}


                            {/* TYPE BADGE */}

                            <div className="media-type-badge">

                              {isImage(item)
                                ? "IMAGE"
                                : isVideo(item)
                                  ? "VIDEO"
                                  : label}

                            </div>


                            {/* OVERLAY */}

                            <div className="media-card-overlay">

                              <span>

                                {isImage(item)
                                  ? "View Image"
                                  : isVideo(item)
                                    ? "Watch Video"
                                    : "View Document"}

                              </span>


                              <strong>

                                ↗

                              </strong>

                            </div>

                          </div>


                          {/* =================================================
                              CARD INFO
                          ================================================= */}

                          {(item.caption ||
                            item.name) && (

                            <div className="media-card-info">

                              <small>

                                {item.folder ||
                                  "MEDIA"}

                              </small>


                              <h3>

                                {item.caption ||
                                  item.name}

                              </h3>

                            </div>

                          )}

                        </article>

                      );

                    }
                  )}

                </div>


                {/* =========================================================
                    PAGINATION
                ========================================================= */}

                {pagination &&
                  pagination.last_page >
                    1 && (

                    <div className="media-pagination">

                      <button
                        type="button"
                        disabled={
                          pagination.current_page <=
                          1
                        }
                        onClick={() =>
                          handlePageChange(
                            pagination.current_page -
                              1
                          )
                        }
                      >
                        ←
                      </button>


                      {Array.from(
                        {
                          length:
                            pagination.last_page,
                        },
                        (
                          _,
                          index
                        ) =>
                          index + 1
                      )
                        .filter(
                          (number) => {

                            if (
                              pagination.last_page <=
                              7
                            ) {

                              return true;

                            }


                            return (
                              number ===
                                1 ||
                              number ===
                                pagination.last_page ||
                              Math.abs(
                                number -
                                  pagination.current_page
                              ) <= 1
                            );

                          }
                        )
                        .map(
                          (
                            number,
                            index,
                            pages
                          ) => {

                            const previous =
                              pages[
                                index - 1
                              ];


                            const showDots =
                              previous &&
                              number -
                                previous >
                                1;


                            return (

                              <React.Fragment
                                key={number}
                              >

                                {showDots && (

                                  <span className="media-page-dots">

                                    ...

                                  </span>

                                )}


                                <button
                                  type="button"
                                  className={
                                    number ===
                                    pagination.current_page
                                      ? "active"
                                      : ""
                                  }
                                  onClick={() =>
                                    handlePageChange(
                                      number
                                    )
                                  }
                                >

                                  {number}

                                </button>

                              </React.Fragment>

                            );

                          }
                        )}


                      <button
                        type="button"
                        disabled={
                          pagination.current_page >=
                          pagination.last_page
                        }
                        onClick={() =>
                          handlePageChange(
                            pagination.current_page +
                              1
                          )
                        }
                      >
                        →
                      </button>

                    </div>

                  )}


                {/* =========================================================
                    PAGINATION SUMMARY
                ========================================================= */}

                {pagination && (

                  <div className="media-pagination-summary">

                    Showing{" "}

                    <strong>
                      {pagination.from ||
                        0}
                    </strong>{" "}

                    –{" "}

                    <strong>
                      {pagination.to ||
                        0}
                    </strong>{" "}

                    of{" "}

                    <strong>
                      {pagination.total ||
                        0}
                    </strong>{" "}

                    media files

                  </div>

                )}

              </>

            )}

        </div>

      </section>


      {/* =============================================================
          LIGHTBOX / MEDIA VIEWER
      ============================================================= */}

      {selectedMedia && (

        <div
          className="media-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Media preview"
          onClick={
            closeLightbox
          }
        >

          {/* CLOSE */}

          <button
            type="button"
            className="media-lightbox-close"
            aria-label="Close media"
            onClick={
              closeLightbox
            }
          >
            ×
          </button>


          {/* CONTENT */}

          <div
            className="media-lightbox-content"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* =======================================================
                IMAGE
            ======================================================= */}

            {isImage(
              selectedMedia
            ) && (

              <div className="media-lightbox-media">

                <img
                  src={getMediaUrl(
                    selectedMedia
                  )}
                  alt={
                    selectedMedia.alt_text ||
                    selectedMedia.name
                  }
                />

              </div>

            )}


            {/* =======================================================
                VIDEO
            ======================================================= */}

            {isVideo(
              selectedMedia
            ) && (

              <div className="media-lightbox-media">

                <video
                  src={getMediaUrl(
                    selectedMedia
                  )}
                  controls
                  autoPlay
                  playsInline
                />

              </div>

            )}


            {/* =======================================================
                DOCUMENT
            ======================================================= */}

            {isDocument(
              selectedMedia
            ) && (

              <div className="media-lightbox-document">

                <div className="media-lightbox-document-icon">

                  {
                    getDocumentIcon(
                      selectedMedia
                    )
                  }

                </div>


                <h3>

                  {
                    selectedMedia.file_name ||
                    selectedMedia.name
                  }

                </h3>


                <p>

                  {getDocumentType(
                    selectedMedia
                  )}

                </p>


                <div className="media-document-actions">

                  <a
                    href={getMediaUrl(
                      selectedMedia
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="media-document-open"
                  >
                    Open Document
                  </a>


                  <button
                    type="button"
                    onClick={() =>
                      handleDownload(
                        selectedMedia
                      )
                    }
                  >
                    Download
                  </button>

                </div>

              </div>

            )}


            {/* =======================================================
                CAPTION
            ======================================================= */}

            {(selectedMedia.caption ||
              selectedMedia.name) && (

              <div className="media-lightbox-caption">

                <strong>

                  {selectedMedia.caption ||
                    selectedMedia.name}

                </strong>


                {selectedMedia.folder && (

                  <span>

                    {selectedMedia.folder}

                  </span>

                )}

              </div>

            )}

          </div>

        </div>

      )}

    </main>

  );

};


export default PublicMedia;
